import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo
} from 'react'

import { LeftCircleOutlined } from '@ant-design/icons'
import { Group, Box } from '@mantine/core'
import {
  Button,
  Select,
  message,
  Skeleton,
  DatePicker,
  Input,
  Alert
} from 'antd'
import moment from 'moment-timezone'
import { useNavigate, useParams } from 'react-router-dom'
import useSWR, { useSWRConfig } from 'swr'

import { getClusterInfo, createTrafficMirrorReport } from '~/api'
import { trafficMirrorReportTelemetry } from '~/telemetry/traffic-mirror-report'

import { formatDuration, parseDuration } from '../shared/utils'
import { ReportTable, getTrafficMirrorReportListKey } from './ReportTable'

// Add local translations for comparison report
// globalAddTranslations(comparisonTranslations, COMPARISON_REPORT_NS)

interface TouchReportFormValue {
  recent?: number
  startTime?: number
  endTime?: number
  clusterId?: string
  durationSeconds?: number
}
const initialFormValues = (): TouchReportFormValue => ({
  recent: 0,
  startTime: 0,
  endTime: 0,
  clusterId: '',
  durationSeconds: 43200 // 默认 12h
})
type TouchReportForm = {
  t1: TouchReportFormValue
  t2: TouchReportFormValue
  reportName?: string
}
const FormContext = createContext<
  [TouchReportForm, React.Dispatch<React.SetStateAction<TouchReportForm>>]
>(null as any)
const useFormInstance = () => useContext(FormContext)

const Page: React.FC = () => {
  const { orgId, cid } = useParams<'orgId' | 'cid'>()
  const navigate = useNavigate()
  const f = useState<TouchReportForm>(() => {
    return {
      t1: initialFormValues(),
      t2: initialFormValues(),
      reportName: ''
    }
  })
  const { data: clusterInfoRes } = useSWR(
    `/orgs/${orgId}/clusters/${cid}`,
    () => getClusterInfo(orgId!, cid!)
  )

  return (
    <div className='flex flex-col h-full'>
      <div className='py-4'>
        <Button
          icon={<LeftCircleOutlined />}
          type='text'
          onClick={() => navigate(-1)}
        ></Button>
        <span className='text-xl ml-2'>Traffic Mirror Report</span>
      </div>

      <div className='rounded-lg bg-white p-6 mb-4'>
        <p className='text-xl'>
          Cluster Name:{' '}
          {clusterInfoRes?.data?.name ? (
            clusterInfoRes?.data?.name
          ) : (
            <Skeleton.Input style={{ width: '150px' }} active />
          )}
        </p>

        <div className='relative'>
          <FormContext.Provider value={f}>
            <CloudTimerangeSelect />
            <CreateReportButton />
          </FormContext.Provider>
        </div>
      </div>

      <div className='rounded-lg bg-white px-6 pt-6'>
        <p className='text-lg'>All Reports</p>
        <ReportTable />
      </div>
    </div>
  )
}

const CreateReportButton = React.memo(() => {
  const { orgId, cid } = useParams<'orgId' | 'cid'>()
  const [f] = useFormInstance()
  const { mutate } = useSWRConfig()
  const [loading, setLoading] = useState(false)

  // 检查所有必填字段是否都已填写
  // 注意：to_now 模式下 durationSeconds 可能为 0，需要特殊处理
  const isFormValid = useMemo(() => {
    return !!(
      f.t1.startTime &&
      f.t1.startTime > 0 &&
      f.t2.clusterId &&
      f.t2.clusterId.trim() !== '' &&
      f.t2.startTime &&
      f.t2.startTime > 0 &&
      f.t2.durationSeconds !== undefined &&
      f.t2.durationSeconds !== null
    )
  }, [f.t1.startTime, f.t2.clusterId, f.t2.startTime, f.t2.durationSeconds])

  // 计算 target end time 并检查是否大于当前时间
  const isTargetEndTimeInvalid = useMemo(() => {
    if (
      !f.t2.startTime ||
      f.t2.durationSeconds === undefined ||
      f.t2.durationSeconds === null
    ) {
      return false
    }

    const now = moment().unix()
    let targetEndTime: number

    // durationSeconds 为 0 表示 "to now" 模式
    if (f.t2.durationSeconds === 0) {
      targetEndTime = now
    } else {
      targetEndTime = f.t2.startTime + f.t2.durationSeconds
    }

    return targetEndTime > now
  }, [f.t2.startTime, f.t2.durationSeconds])

  return (
    <Box>
      <Button
        style={{ position: 'absolute !important' as any }}
        type='primary'
        disabled={loading || !isFormValid || isTargetEndTimeInvalid}
        onClick={async () => {
          // 验证表单
          if (!f.t1.startTime || !f.t2.startTime || !f.t2.clusterId) {
            message.error('Please fill in all required fields')
            return
          }

          try {
            setLoading(true)
            const now = Math.floor(Date.now() / 1000)

            // 计算时间范围
            const startTime1 = f.t1.startTime!
            let endTime1 = startTime1
            let startTime2 = f.t2.startTime!
            let endTime2 = startTime2

            // 根据 duration 计算结束时间
            // durationSeconds 为 0 表示 "to now" 模式
            if (f.t2.durationSeconds === 0) {
              // to now: target end time 为当前时间，current end time 保持相同的时间长度
              endTime2 = now
              const duration = endTime2 - startTime2
              endTime1 = startTime1 + duration
            } else if (f.t2.durationSeconds && f.t2.durationSeconds > 0) {
              // 从 startTime2 开始，持续 durationSeconds 秒
              endTime2 = startTime2 + f.t2.durationSeconds
              // t1 使用相同的时间长度
              endTime1 = startTime1 + f.t2.durationSeconds
            } else {
              message.error('Please select valid duration')
              return
            }

            const params = {
              startTime1,
              startTime2,
              endTime1,
              endTime2,
              clusterID1: cid!,
              clusterID2: f.t2.clusterId!,
              ...(f.reportName && f.reportName.trim() !== ''
                ? { name: f.reportName.trim() }
                : {})
            }
            await createTrafficMirrorReport(orgId!, cid!, params)
            mutate(getTrafficMirrorReportListKey(orgId!, cid!, 1))
            trafficMirrorReportTelemetry.createReport(orgId!, cid!, params)
            message.loading(
              'Diagnosis Engine is generating report, it may take about few minutes.'
            )
          } finally {
            setLoading(false)
          }
        }}
      >
        Create Report
      </Button>
      {isTargetEndTimeInvalid && (
        <Alert
          message='Target cluster end time must be less than or equal to the current time'
          type='error'
          showIcon
          style={{ marginTop: '8px' }}
        />
      )}
    </Box>
  )
})

const disabledDate: (date: moment.Moment) => boolean = (current) => {
  const biggerThanNow = current && current > moment().endOf('days')
  return biggerThanNow
}

const MAX_DURATION_HOURS = 72

const RECENT_DURATION_OPTIONS = [
  { label: '12h', value: 43200 },
  { label: '24h', value: 86400 },
  { label: 'To Now', value: 'to_now' },
  { label: 'Custom', value: 'custom' }
]

const CUSTOM_DURATION_VALUE = 'custom'
const TO_NOW_VALUE = 'to_now'

const CloudTimerangeSelect = () => {
  const { cid } = useParams<'orgId' | 'cid'>()
  const [f, setF] = useFormInstance()
  const [datetimeDiffInput, setDatetimeDiffInput] = useState<string>('')
  const [targetClusterStartTime, setTargetClusterStartTime] =
    useState<moment.Moment | null>(null)
  const [isCustomDuration, setIsCustomDuration] = useState(false)
  const [customHours, setCustomHours] = useState<number | undefined>(undefined)
  const [isToNow, setIsToNow] = useState(false)

  // localStorage key
  const storageKey = `traffic_report_create_form_${cid}`

  // 从 localStorage 读取保存的值
  useEffect(() => {
    if (!cid) return

    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved)
        const updates: Partial<TouchReportForm> = {
          t1: { ...initialFormValues() },
          t2: { ...initialFormValues() }
        }

        if (parsed.targetClusterId) {
          updates.t2!.clusterId = parsed.targetClusterId
        }
        if (parsed.targetStartTime && parsed.targetStartTime > 0) {
          updates.t2!.startTime = parsed.targetStartTime
          setTargetClusterStartTime(moment.unix(parsed.targetStartTime))
        }
        if (parsed.datetimeDiff) {
          setDatetimeDiffInput(parsed.datetimeDiff)
          // 如果有 targetStartTime 和 datetimeDiff，计算 t1.startTime
          if (parsed.targetStartTime && parsed.targetStartTime > 0) {
            const diffSeconds = parseDuration(parsed.datetimeDiff)
            if (diffSeconds !== null && diffSeconds >= 0) {
              const calculatedStartTime = parsed.targetStartTime - diffSeconds
              if (calculatedStartTime > 0) {
                updates.t1!.startTime = calculatedStartTime
              }
            }
          }
        }

        setF((prev) => ({
          ...prev,
          t1: { ...prev.t1, ...updates.t1 },
          t2: { ...prev.t2, ...updates.t2 }
        }))
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, storageKey])

  // 保存到 localStorage
  useEffect(() => {
    if (!cid) return

    try {
      const dataToSave: {
        targetClusterId?: string
        targetStartTime?: number
        datetimeDiff?: string
      } = {}

      if (f.t2.clusterId && f.t2.clusterId.trim() !== '') {
        dataToSave.targetClusterId = f.t2.clusterId
      }
      if (f.t2.startTime && f.t2.startTime > 0) {
        dataToSave.targetStartTime = f.t2.startTime
      }
      if (datetimeDiffInput && datetimeDiffInput.trim() !== '') {
        dataToSave.datetimeDiff = datetimeDiffInput
      }

      if (Object.keys(dataToSave).length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(dataToSave))
      } else {
        // 如果所有值都为空，清除 localStorage
        localStorage.removeItem(storageKey)
      }
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
    }
  }, [cid, storageKey, f.t2.clusterId, f.t2.startTime, datetimeDiffInput])

  // 格式化时间
  const formatDateTimeUTC = (seconds: number): string => {
    return moment.unix(seconds).format('YYYY-MM-DD HH:mm:ss')
  }

  // 计算并显示时间段
  const currentTimeRangeDisplay = useMemo(() => {
    if (!f.t1.startTime) return null

    const start = moment.unix(f.t1.startTime)
    let end: moment.Moment
    if (isToNow && f.t2.startTime) {
      // to now: current end time = current start time + (now - target start time)
      const targetDuration = moment().unix() - f.t2.startTime
      end = start.clone().add(targetDuration, 'seconds')
    } else if (f.t2.durationSeconds) {
      end = start.clone().add(f.t2.durationSeconds, 'seconds')
    } else {
      return null
    }

    return `${formatDateTimeUTC(f.t1.startTime)} ~ ${formatDateTimeUTC(
      end.unix()
    )}`
  }, [f.t1.startTime, f.t2.startTime, f.t2.durationSeconds, isToNow])

  const targetTimeRangeDisplay = useMemo(() => {
    if (!f.t2.startTime) return null

    const start = moment.unix(f.t2.startTime)
    let end: moment.Moment
    if (isToNow) {
      end = moment()
    } else if (f.t2.durationSeconds) {
      end = start.clone().add(f.t2.durationSeconds, 'seconds')
    } else {
      return null
    }

    return `${formatDateTimeUTC(f.t2.startTime)} ~ ${formatDateTimeUTC(
      end.unix()
    )}`
  }, [f.t2.startTime, f.t2.durationSeconds, isToNow])

  // 计算 Datetime Diff: start time2 - start time1 (用于显示)
  const datetimeDiff = useMemo(() => {
    if (!f.t1.startTime || !f.t2.startTime) return null
    const diff = f.t2.startTime - f.t1.startTime
    if (diff === 0) return '0s'
    return formatDuration(Math.abs(diff))
  }, [f.t1.startTime, f.t2.startTime])

  // 根据 Datetime Diff 输入和 t2.startTime 计算 t1.startTime
  useEffect(() => {
    if (
      datetimeDiffInput &&
      datetimeDiffInput.trim() !== '' &&
      f.t2.startTime &&
      f.t2.startTime > 0
    ) {
      const diffSeconds = parseDuration(datetimeDiffInput)
      if (diffSeconds !== null && diffSeconds >= 0) {
        // t1.startTime = t2.startTime - diffSeconds
        const calculatedStartTime = f.t2.startTime - diffSeconds
        if (calculatedStartTime > 0) {
          setF((prev) => {
            // 避免不必要的更新
            if (prev.t1.startTime === calculatedStartTime) {
              return prev
            }
            return {
              ...prev,
              t1: { ...prev.t1, startTime: calculatedStartTime }
            }
          })
        }
      }
    }
  }, [datetimeDiffInput, f.t2.startTime, setF])

  return (
    <>
      {/* Report Name */}
      <Group className='mb-4'>
        <div className='min-w-[330px]'>
          <span className='mr-2 font-medium'>Report Name:</span>
          <Input
            className='rounded-lg'
            style={{ width: 230 }}
            placeholder='Enter report name (optional)'
            value={f.reportName || ''}
            onChange={(e) => {
              setF((prev) => ({
                ...prev,
                reportName: e.target.value
              }))
            }}
          />
        </div>
      </Group>

      {/* Current Cluster */}
      <Group className='mb-4'>
        <div className='min-w-[330px]'>
          <span className='mr-2 font-medium'>Current Cluster ID:</span>
          {cid}
        </div>

        <div>
          <span className='mr-2 font-medium'>Start Time:</span>
          <span className='ml-2 text-gray-600'>
            {!!f.t1.startTime && f.t1.startTime > 0
              ? formatDateTimeUTC(f.t1.startTime)
              : '-'}
          </span>
        </div>
      </Group>

      {/* Target Cluster Input and Start Time */}
      <Group className='mb-4'>
        <div className='min-w-[330px]'>
          <span className='mr-2 font-medium'>Target Cluster ID:</span>
          <Input
            className='mr-2'
            style={{ width: 200 }}
            placeholder='Enter target cluster ID'
            onChange={(e) => {
              setF((prev) => ({
                ...prev,
                t2: { ...prev.t2, clusterId: e.target.value }
              }))
            }}
            value={f.t2.clusterId}
          />
        </div>
        <div>
          <span className='mr-2 font-medium'>Start Time:</span>
          <DatePicker
            showTime
            className='rounded-lg'
            disabledDate={disabledDate}
            value={targetClusterStartTime}
            onChange={(date) => {
              setTargetClusterStartTime(date)
              // 将用户输入视为本地时间，转换为时间戳
              const newStartTime = date
                ? moment({
                    year: date.year(),
                    month: date.month(),
                    date: date.date(),
                    hour: date.hour(),
                    minute: date.minute(),
                    second: date.second()
                  }).unix()
                : 0
              // 如果已有 datetime diff 输入，重新计算 t1.startTime
              if (
                datetimeDiffInput &&
                datetimeDiffInput.trim() !== '' &&
                newStartTime > 0
              ) {
                const diffSeconds = parseDuration(datetimeDiffInput)
                if (diffSeconds !== null && diffSeconds >= 0) {
                  const calculatedStartTime = newStartTime - diffSeconds
                  if (calculatedStartTime > 0) {
                    setF((prev) => ({
                      ...prev,
                      t2: { ...prev.t2, startTime: newStartTime },
                      t1: { ...prev.t1, startTime: calculatedStartTime }
                    }))
                    return
                  }
                }
              }
              // 如果没有 datetime diff 或计算失败，只更新 t2.startTime
              setF((prev) => ({
                ...prev,
                t2: { ...prev.t2, startTime: newStartTime }
              }))
            }}
            format='YYYY-MM-DD HH:mm:ss'
          />
        </div>
        <div>
          <span className='mr-2 font-medium'>Datetime Diff:</span>
          <Input
            className='rounded-lg'
            style={{ width: 197 }}
            placeholder='e.g., 1d 13h 18m 16s'
            value={datetimeDiffInput}
            onChange={(e) => {
              setDatetimeDiffInput(e.target.value)
            }}
            onBlur={(e) => {
              const value = e.target.value.trim()
              if (value && f.t2.startTime && f.t2.startTime > 0) {
                const diffSeconds = parseDuration(value)
                if (diffSeconds === null || diffSeconds < 0) {
                  message.error(
                    'Invalid datetime diff format. Use format like: 1d13h18m16s or 1d 13h 18m 16s'
                  )
                  setDatetimeDiffInput('')
                  return
                }
                const calculatedStartTime = f.t2.startTime - diffSeconds
                if (calculatedStartTime <= 0) {
                  message.error('Calculated start time must be greater than 0')
                  setDatetimeDiffInput('')
                  return
                }
                setF((prev) => ({
                  ...prev,
                  t1: { ...prev.t1, startTime: calculatedStartTime }
                }))
              } else if (value === '') {
                // 清空时，也清空 t1.startTime
                setF((prev) => ({
                  ...prev,
                  t1: { ...prev.t1, startTime: 0 }
                }))
              }
            }}
          />
        </div>
      </Group>

      {/* Comparison Duration Time */}
      <div className='mb-4'>
        <span className='mr-2 font-medium'>Comparison Duration Time:</span>
        <Select
          className='mr-2'
          style={{ width: 150 }}
          value={
            isCustomDuration
              ? CUSTOM_DURATION_VALUE
              : isToNow
              ? TO_NOW_VALUE
              : f.t2.durationSeconds || 43200
          }
          onChange={(v) => {
            if (v === CUSTOM_DURATION_VALUE) {
              setIsCustomDuration(true)
              setIsToNow(false)
              // 如果之前有自定义小时数，保持它
              if (customHours !== undefined && customHours > 0) {
                setF((prev) => ({
                  ...prev,
                  t2: {
                    ...prev.t2,
                    durationSeconds: customHours * 3600
                  }
                }))
              } else {
                // 如果没有自定义值，默认设置为 1 小时
                setCustomHours(1)
                setF((prev) => ({
                  ...prev,
                  t2: {
                    ...prev.t2,
                    durationSeconds: 3600
                  }
                }))
              }
            } else if (v === TO_NOW_VALUE) {
              setIsCustomDuration(false)
              setIsToNow(true)
              setCustomHours(undefined)
              setF((prev) => ({
                ...prev,
                t2: { ...prev.t2, durationSeconds: 0 }
              }))
            } else {
              setIsCustomDuration(false)
              setIsToNow(false)
              setCustomHours(undefined)
              setF((prev) => ({
                ...prev,
                t2: { ...prev.t2, durationSeconds: v as number }
              }))
            }
          }}
        >
          {RECENT_DURATION_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
        {isCustomDuration && (
          <>
            <Input
              type='number'
              min={1}
              max={MAX_DURATION_HOURS}
              step={1}
              placeholder={`Enter hours (max ${MAX_DURATION_HOURS})`}
              style={{ width: 200, marginLeft: 8 }}
              value={customHours !== undefined ? customHours : ''}
              onChange={(e) => {
                const inputValue = e.target.value
                if (inputValue === '' || inputValue === '.') {
                  setCustomHours(undefined)
                  setF((prev) => ({
                    ...prev,
                    t2: { ...prev.t2, durationSeconds: 0 }
                  }))
                  return
                }
                const value = parseFloat(inputValue)
                if (isNaN(value)) {
                  return
                }
                if (value > MAX_DURATION_HOURS) {
                  message.warning(
                    `Maximum duration is ${MAX_DURATION_HOURS} hours`
                  )
                  setCustomHours(MAX_DURATION_HOURS)
                  setF((prev) => ({
                    ...prev,
                    t2: {
                      ...prev.t2,
                      durationSeconds: MAX_DURATION_HOURS * 3600
                    }
                  }))
                  return
                }
                if (value <= 0) {
                  message.warning('Duration must be greater than 0')
                  return
                }
                setCustomHours(value)
                setF((prev) => ({
                  ...prev,
                  t2: { ...prev.t2, durationSeconds: value * 3600 }
                }))
              }}
              onBlur={(e) => {
                const value = parseFloat(e.target.value)
                if (isNaN(value) || value <= 0) {
                  message.error('Please enter a valid number greater than 0')
                  setCustomHours(undefined)
                  setF((prev) => ({
                    ...prev,
                    t2: { ...prev.t2, durationSeconds: 0 }
                  }))
                }
              }}
            />
            <span className='ml-1'>h</span>
          </>
        )}
      </div>

      {/* Time Range Display */}
      {(currentTimeRangeDisplay || targetTimeRangeDisplay) && (
        <div className='mb-4 p-3 bg-gray-50 rounded-lg'>
          <div className='mb-2'>
            <span className='font-medium mr-2 inline-block min-w-[185px]'>
              Current Cluster Time Range:
            </span>
            <span className='text-gray-700'>
              {currentTimeRangeDisplay || '-'}
            </span>
          </div>
          <div className='mb-2'>
            <span className='font-medium mr-2 inline-block min-w-[185px]'>
              Target Cluster Time Range:
            </span>
            <span className='text-gray-700'>
              {targetTimeRangeDisplay || '-'}
            </span>
          </div>
          <div className='mb-2'>
            <span className='font-medium mr-2 inline-block min-w-[185px]'>
              Datetime Diff:
            </span>
            <span className='text-gray-700'>{datetimeDiff || '-'}</span>
          </div>
          <div>
            <span className='font-medium mr-2 inline-block min-w-[185px]'>
              Duration:
            </span>
            <span className='text-gray-700'>
              {isToNow
                ? 'to now'
                : f.t2.durationSeconds && f.t2.durationSeconds > 0
                ? formatDuration(f.t2.durationSeconds)
                : '-'}
            </span>
          </div>
        </div>
      )}
    </>
  )
}

export default Page
